import { ExternalLink, Loader, Merge } from "lucide-react";
import { useState } from "react";

import type { GetLegalSubpoenaExhibitsResponse } from "@upclaim/sdk/src/data-contracts";

import { Button } from "@/components/ui/button";

import type { ClaimWithRelations } from "../../api/use-claim";
import type { useLegalSubpoena } from "../../api/use-claim-actions";
import {
  useGenerateLegalSubpoenaProject,
  useLegalSubpoenaExhibits,
  useMergeLegalSubpoena,
  useSaveLegalSubpoenaExhibits,
} from "../../api/use-claim-actions";
import { openBoxFile } from "../../lib/open-box";
import type { ClaimDocumentOption } from "../exhibits/exhibit-review-utils";
import { LegalSubpoenaExhibitsReview } from "../exhibits/legal-subpoena-exhibits-review";

type LegalSubpoena = NonNullable<ReturnType<typeof useLegalSubpoena>["data"]>;
type Exhibit = GetLegalSubpoenaExhibitsResponse["exhibits"][number];

interface GenerateLegalSubpoenaProps {
  claim: ClaimWithRelations;
  /** null → new subpoena project (the default numbering is fetched). */
  legalSubpoena: LegalSubpoena | null;
}

/** What the API stores for one exhibit — the review's save and the generation send the same. */
function toExhibitInput({ claimDocumentId, exhibitNumber, exhibitSubNumber, label }: Exhibit) {
  return { claimDocumentId, exhibitNumber, exhibitSubNumber, label };
}

/** Port of overview/GenerateLegalSubpeona.vue — project generation / final merge. */
export function GenerateLegalSubpoena({ claim, legalSubpoena }: GenerateLegalSubpoenaProps) {
  const generateProject = useGenerateLegalSubpoenaProject(claim.id);
  const mergeLegalSubpoena = useMergeLegalSubpoena(claim.id);
  const saveExhibits = useSaveLegalSubpoenaExhibits(claim.id);
  const { data: exhibitsData } = useLegalSubpoenaExhibits(claim.id);

  // What the review view currently shows — generation sends exactly this order.
  const [draftExhibits, setDraftExhibits] = useState<Exhibit[] | null>(null);
  // Generation reads the *stored* numbering, so it must not run while an edit is
  // still unsaved or in flight — it would build the bordereau from stale numbers.
  const [hasPendingEdits, setHasPendingEdits] = useState(false);
  const exhibits = draftExhibits ?? exhibitsData?.exhibits ?? [];
  const isEditable = exhibitsData?.isEditable ?? true;

  // Served by the API, with the bordereau wording each document would carry — the
  // review view offers whichever of these are not already in the bordereau.
  const claimDocuments: ClaimDocumentOption[] = exhibitsData?.claimDocuments ?? [];

  async function generateLegalSubpoenaProject() {
    // The arrangement on screen, numbers and labels included — on a first generation
    // there is no subpoena row it could have been saved to beforehand.
    const result = await generateProject.mutateAsync(exhibits.map(toExhibitInput)).catch(() => null);

    // Open the generated .docx once the mutation resolves — GenerateLegalSubpeona.vue parity (a plain
    // window.open in the handler). notify.promise already toasts any error.
    if (result?.docxBoxFileId) {
      setDraftExhibits(null);
      openBoxFile(result.docxBoxFileId);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {exhibitsData ? (
        <LegalSubpoenaExhibitsReview
          exhibits={exhibitsData.exhibits}
          claimDocuments={claimDocuments}
          isEditable={isEditable}
          isStored={exhibitsData.isStored}
          isSaving={saveExhibits.isPending}
          onChange={setDraftExhibits}
          onPendingChange={setHasPendingEdits}
          // Saving needs an existing subpoena row to attach the numbering to; before
          // the first generation the arrangement travels with the generation itself.
          onSave={legalSubpoena ? (next) => saveExhibits.mutate(next.map(toExhibitInput)) : undefined}
        />
      ) : null}

      <div>
        {!legalSubpoena ? (
          <Button onClick={generateLegalSubpoenaProject} disabled={generateProject.isPending}>
            {generateProject.isPending ? <Loader className="animate-spin" /> : null}
            Générer le projet d'assignation
          </Button>
        ) : (
          <div className="inline-flex">
            <div className="mr-4">
              <Button
                onClick={generateLegalSubpoenaProject}
                disabled={generateProject.isPending || hasPendingEdits}
                title={hasPendingEdits ? "Enregistrement de la numérotation en cours…" : undefined}
              >
                {generateProject.isPending ? <Loader className="animate-spin" /> : null}
                Régénérer le projet
              </Button>
            </div>
            <div className="mr-4">
              {/* The merge attaches the stored pieces in their stored order — same wait as Régénérer. */}
              <Button
                onClick={() => mergeLegalSubpoena.mutate()}
                disabled={mergeLegalSubpoena.isPending || hasPendingEdits}
                title={hasPendingEdits ? "Enregistrement de la numérotation en cours…" : undefined}
              >
                {mergeLegalSubpoena.isPending ? <Loader className="animate-spin" /> : <Merge />}
                Fusionner le document final
              </Button>
            </div>
            {legalSubpoena.docxFile?.boxFileId ? (
              <Button
                variant="outline"
                onClick={() => openBoxFile(legalSubpoena.docxFile?.boxFileId as string)}
              >
                <ExternalLink />
                Ouvrir le fichier .docx
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
